import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Crown, Check, Zap, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { toast } from 'sonner';
import { Capacitor } from '@capacitor/core';
import { RevenueCatUI, PAYWALL_RESULT } from '@revenuecat/purchases-capacitor-ui';
import { Purchases } from '@revenuecat/purchases-capacitor';
import { Sentry } from '@/lib/sentry';

export interface PremiumModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reason?: 'limit' | 'feature' | 'companies' | 'photos' | 'ads' | 'reports' | 'color';
  customMessage?: string;
}

// Price IDs from Stripe (used for web fallback)
const PRICE_IDS = {
  monthly: 'price_1SpaV2PBMOXo8jRTQvcAKgPK', // R$ 19,90/mês
  quarterly: 'price_1SpaXoPBMOXo8jRTvS9woEH6', // R$ 79,90/3 meses
  semiannual: 'price_1SpaYVPBMOXo8jRTa09JE9Nf', // R$ 149,90/6 meses
  annual: 'price_1SpaYyPBMOXo8jRTMMYDF4v1', // R$ 259,90/ano
};

// Stripe Payment Link (fallback for web)
const STRIPE_PAYMENT_LINK = 'https://buy.stripe.com/test_cNi7sNfhzcCh67C7xlcjS00';

/** @deprecated Prefer `useGlobalPaywall()` so the app uses the single global paywall instance. */
export function PremiumModal({ open, onOpenChange, reason = 'limit', customMessage }: PremiumModalProps) {
  const { user, refreshProfile } = useAuth();
  const [hasTriggered, setHasTriggered] = useState(false);

  // Native RevenueCat Paywall for mobile
  const showNativePaywall = async () => {
    try {
      const { result } = await RevenueCatUI.presentPaywall();
      
      if (result === PAYWALL_RESULT.PURCHASED || result === PAYWALL_RESULT.RESTORED) {
        const { customerInfo } = await Purchases.getCustomerInfo();

        const entitlement = customerInfo.entitlements.active['orcamigo_pro_planos'];
        // RC returns periodType 'TRIAL' or 'INTRO' for free-trial and intro pricing.
        const rcPeriodType = (entitlement as unknown as { periodType?: string })?.periodType;
        const isTrialPurchase = rcPeriodType === 'TRIAL' || rcPeriodType === 'INTRO';

        if (entitlement) {
          if (user) {
            const baseUpdates: Record<string, unknown> = {
              plan_type: 'pro',
              subscription_end_date: entitlement.expirationDate ?? null,
              is_premium: true,
            };
            const updates: Record<string, unknown> = isTrialPurchase
              ? {
                  ...baseUpdates,
                  subscription_status: 'trialing',
                  trial_started_at: new Date().toISOString(),
                  trial_ends_at: entitlement.expirationDate ?? null,
                  trial_source: 'paywall',
                }
              : { ...baseUpdates, subscription_status: 'active' };

            const { error } = await supabase
              .from('profiles')
              .update(updates)
              .eq('id', user.id);

            if (error) {
              console.error('Error updating profile:', error);
              toast.error("Compra realizada, mas erro ao atualizar perfil. Reinicie o app.");
            } else {
              if (isTrialPurchase) {
                toast.success("Trial de 7 dias ativado! 🎉 Aproveite o Orçamigo PRO.");
              } else {
                toast.success("Parabéns! Você agora é PRO! 🎉");
              }
              await refreshProfile();
            }
          }
        } else {
          toast.success("Parabéns! Você agora é PRO! 🎉");
          await refreshProfile();
        }
      } else if (result === PAYWALL_RESULT.ERROR) {
        toast.error("Erro ao carregar ofertas. Tente novamente.");
      }
    } catch (e) {
      console.error("Erro no Paywall:", e);
      Sentry.captureException(e, { extra: { context: 'showNativePaywall' } });
      toast.error("Erro ao abrir a tela de assinatura.");
    } finally {
      onOpenChange(false);
    }
  };

  // Web Stripe checkout fallback
  const handleWebCheckout = async () => {
    if (!user) {
      toast.warning('Você precisa estar logado para assinar o plano PRO.');
      onOpenChange(false);
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: { priceId: PRICE_IDS.monthly },
      });

      if (error) {
        console.error('Edge Function error, using Payment Link fallback:', error);
        Sentry.captureException(error, { extra: { context: 'create-checkout-session' } });
        window.open(STRIPE_PAYMENT_LINK, '_blank');
      } else if (data?.url) {
        window.open(data.url, '_blank');
      } else {
        window.open(STRIPE_PAYMENT_LINK, '_blank');
      }
    } catch (err) {
      console.error('Checkout error, using Payment Link fallback:', err);
      Sentry.captureException(err, { extra: { context: 'handleWebCheckout' } });
      window.open(STRIPE_PAYMENT_LINK, '_blank');
    } finally {
      onOpenChange(false);
    }
  };

  // When modal opens, immediately trigger the paywall
  useEffect(() => {
    if (open && !hasTriggered) {
      setHasTriggered(true);
      const isNative = Capacitor.isNativePlatform();
      
      if (isNative) {
        showNativePaywall();
      } else {
        handleWebCheckout();
      }
    }
    
    if (!open) {
      setHasTriggered(false);
    }
  }, [open]);

  // No modal UI - the paywall/checkout opens directly
  return null;
}
